import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  ALWAYS_OPEN_TIME,
  DEFAULT_SCHEDULE,
  MIDNIGHT,
  SCHEDULE_DAY_OPTIONS,
  VIEW_MODE,
  TIMELINE_COLORS,
} from 'constants/menuManagement';
import { calculateDefaultDate } from 'helpers/menuManagementHours';

import { Alert, Button, Stack, Text } from '@mantine/core';

import { generateTempId } from 'helpers/utils';
import {
  getTimeOptions,
  getDayOptions,
  isDayClosed,
  isDayOpened24Hours,
  splitRolloverHours,
  hasRolloverHours,
  resolvePrimaryId,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';
import HolidayHoursInputView from './HolidayHours/HolidayHoursInputView';
import StandardHoursInputView from './StandardHours/StandardHoursInputView';
import StandardTimeline from './StandardHours/StandardTimeline';
import HolidayTimeline from './HolidayHours/HolidayTimeline';
import { PlusCircleIconSm, BellIconMd } from '@chownow/pantry/icons';

// Use UUID for existing schedules generated from BE
// Or tempId for newly added schedules (local) so multiple entries won't have the same id
const matchesScheduleId = (schedule, id) => {
  const scheduleId = resolvePrimaryId(schedule, schedule.isPeriod);
  return scheduleId === id;
};

export default function ScheduleSelect({
  schedules,
  setSchedules,
  isSpecial,
  viewMode,
  showAddButton = true,
  compact = false,
  disabled = false,
  timelineColor = TIMELINE_COLORS.PICKUP,
  getValidationErrors,
  onRolloverChange,
}) {
  const [savedCustomHours, setSavedCustomHours] = useState({});
  const timeOptions = getTimeOptions();
  const dayOptions = getDayOptions();

  // Check if we have any schedule data at all (imported menus may not have any)
  const hasAnySchedules = schedules.some(schedule => !schedule.isDeleted);

  // if we have any schedules but still missing some days, and we're not in special hours mode,
  // populate the missing days with default hours
  useEffect(() => {
    if (!isSpecial && hasAnySchedules) {
      const activeSchedules = schedules.filter(schedule => !schedule.isDeleted);
      const existingDays = new Set(activeSchedules.map(schedule => schedule.dayIndex));

      // Find missing days and create default schedules for them
      const missingDays = SCHEDULE_DAY_OPTIONS.filter(day => !existingDays.has(day.value)).map(
        day => ({
          dayIndex: day.value,
          startTime: MIDNIGHT,
          endTime: MIDNIGHT,
          tempId: generateTempId(),
        }),
      );

      if (missingDays.length > 0) {
        setSchedules(prev => [...prev, ...missingDays]);
      }
    }
  }, [isSpecial, schedules, setSchedules, hasAnySchedules]);

  const handleChange = (val, id, type) => {
    const options = type === 'dayIndex' ? dayOptions : timeOptions;

    // if setting end time to 12:00 AM, use 23:59 for the api
    if (type === 'endTime' && val === '12:00 AM') {
      setSchedules(prevSchedules =>
        prevSchedules.map(schedule => {
          if (matchesScheduleId(schedule, id)) {
            return { ...schedule, endTime: '23:59' };
          }
          return schedule;
        }),
      );
      return;
    }

    const apiValue =
      type === 'appliedDate'
        ? { value: dayjs(val).format('YYYY-MM-DD') }
        : options.find(option => option.displayValue === val);

    setSchedules(prevSchedules => {
      const targetSchedule = prevSchedules.find(schedule => matchesScheduleId(schedule, id));
      const newValue = apiValue.value;

      // Check if we're setting hours to closed (12am-12am)
      const isSettingToClosed =
        (type === 'startTime' && newValue === MIDNIGHT && targetSchedule.endTime === MIDNIGHT) ||
        (type === 'endTime' && newValue === MIDNIGHT && targetSchedule.startTime === MIDNIGHT);

      return prevSchedules.map(schedule => {
        if (matchesScheduleId(schedule, id)) {
          return { ...schedule, [type]: newValue };
        }

        // If setting primary hours to closed, delete any periods for that day
        if (
          isSettingToClosed &&
          !targetSchedule.isPeriod &&
          schedule.dayIndex === targetSchedule.dayIndex &&
          schedule.isPeriod
        ) {
          return { ...schedule, isDeleted: true };
        }

        return schedule;
      });
    });
  };

  const saveCustomHours = (id, schedule) => {
    setSavedCustomHours(prev => ({
      ...prev,
      [id]: {
        startTime: schedule.startTime,
        endTime: schedule.endTime,
      },
    }));
  };

  const getRestoredHours = id => {
    const savedHours = savedCustomHours[id];
    if (!savedHours) {
      return {
        startTime: DEFAULT_SCHEDULE.startTime,
        endTime: DEFAULT_SCHEDULE.endTime,
      };
    }

    return {
      startTime: savedHours.startTime,
      endTime: savedHours.endTime,
    };
  };

  const set24HourSchedule = schedule => ({
    ...schedule,
    startTime: MIDNIGHT,
    endTime: ALWAYS_OPEN_TIME,
  });

  const handle24Hours = id => {
    const schedule = schedules.find(sched => matchesScheduleId(sched, id));
    const isAlreadyOpenAllDay = isDayOpened24Hours(schedule);

    if (!isAlreadyOpenAllDay && !isDayClosed(schedule)) {
      saveCustomHours(id, schedule);
    }

    setSchedules(prevSchedules =>
      prevSchedules.map(sched => {
        if (!matchesScheduleId(sched, id)) return sched;
        return isAlreadyOpenAllDay
          ? { ...sched, ...getRestoredHours(id) }
          : set24HourSchedule(sched);
      }),
    );
  };

  const handleDeleteHours = id => {
    // remove saved hours for the deleted schedule
    setSavedCustomHours(prev => {
      const newSavedHours = { ...prev };
      delete newSavedHours[id];
      return newSavedHours;
    });

    setSchedules(prevSchedules =>
      // eslint-disable-next-line no-confusing-arrow
      prevSchedules.map(schedule =>
        matchesScheduleId(schedule, id) ? { ...schedule, isDeleted: true } : schedule,
      ),
    );
  };

  const handleDayToggle = (prevSchedules, targetSchedule, id, isAlreadyClosed) => {
    const scheduleToUpdate = prevSchedules.find(sched => matchesScheduleId(sched, id));

    // Save current hours before closing (but not if it's 24 hours or already closed)
    if (!isAlreadyClosed && !isDayOpened24Hours(scheduleToUpdate)) {
      saveCustomHours(id, scheduleToUpdate);
    }

    // If opening, restore saved hours, otherwise set to midnight
    const newHours = isAlreadyClosed
      ? getRestoredHours(id)
      : { startTime: MIDNIGHT, endTime: MIDNIGHT };

    return prevSchedules.map(schedule => {
      // Don't update schedules for other days
      if (schedule.dayIndex !== targetSchedule.dayIndex) {
        return schedule;
      }

      // Update target schedule
      if (matchesScheduleId(schedule, id)) {
        return { ...schedule, ...newHours };
      }

      // Delete periods when closing a regular day
      if (!isSpecial && schedule.isPeriod && !isAlreadyClosed) {
        return { ...schedule, isDeleted: true };
      }

      return schedule;
    });
  };

  const handleToggle = id => {
    setSchedules(prevSchedules => {
      const targetSchedule = prevSchedules.find(schedule => matchesScheduleId(schedule, id));
      const isAlreadyClosed = isDayClosed(targetSchedule);

      return handleDayToggle(prevSchedules, targetSchedule, id, isAlreadyClosed);
    });
  };

  const handleAddHours = () => {
    // Create schedules for all days with default hours (9am-5pm)
    const newSchedules = SCHEDULE_DAY_OPTIONS.map(day => ({
      tempId: generateTempId(),
      dayIndex: day.value,
      startTime: DEFAULT_SCHEDULE.startTime,
      endTime: DEFAULT_SCHEDULE.endTime,
    }));
    setSchedules(newSchedules);
  };

  const handleAddHoliday = () => {
    const updateSchedules = [
      ...schedules,
      {
        tempId: generateTempId(),
        appliedDate: calculateDefaultDate(dayjs().add(1, 'day').format('YYYY-MM-DD'), schedules),
        startTime: DEFAULT_SCHEDULE.startTime,
        endTime: DEFAULT_SCHEDULE.endTime,
        isDeleted: false,
      },
    ];
    // Sort schedules only when adding a new holiday
    const newSortedSchedules = updateSchedules.sort((a, b) => {
      const dateDiff = new Date(a.appliedDate) - new Date(b.appliedDate);
      if (dateDiff !== 0) return dateDiff;
      // If dates are equal, sort by startTime
      return a.startTime.localeCompare(b.startTime);
    });
    setSchedules(newSortedSchedules);
  };

  const handleAddPeriod = scheduleId => {
    const schedule = schedules.find(existingSchedule =>
      matchesScheduleId(existingSchedule, scheduleId),
    );
    if (!schedule) return;

    const newSchedule = {
      dayIndex: schedule.dayIndex,
      startTime: DEFAULT_SCHEDULE.startTime,
      endTime: DEFAULT_SCHEDULE.endTime,
      tempId: generateTempId(),
      isPeriod: true,
    };

    setSchedules(prevSchedules => [...prevSchedules, newSchedule]);
  };

  const handleTimeRangeChange = (id, [startTime, endTime]) => {
    setSchedules(prevSchedules =>
      // eslint-disable-next-line no-confusing-arrow
      prevSchedules.map(schedule =>
        matchesScheduleId(schedule, id) ? { ...schedule, startTime, endTime } : schedule,
      ),
    );
  };

  // only display non-deleted schedules
  const visibleSchedules = schedules.filter(schedule => !schedule.isDeleted);

  // check if any schedules have rollover hours
  const hasRollover = visibleSchedules.some(schedule => hasRolloverHours(schedule));

  useEffect(() => {
    onRolloverChange?.(hasRollover);
  }, [hasRollover, onRolloverChange]);

  const getTransformedSchedulesForTimeline = inputSchedules =>
    inputSchedules.flatMap(schedule => {
      if (schedule.isDeleted) return [schedule];
      return splitRolloverHours(schedule, { forBackend: false, isSpecial });
    });

  // split rollover hours locally for timeline view, leave input view the same
  const transformedSchedules =
    viewMode === VIEW_MODE.TIMELINE
      ? getTransformedSchedulesForTimeline(visibleSchedules)
      : visibleSchedules;

  const renderContent = () => {
    if (viewMode === VIEW_MODE.INPUT) {
      const inputViewProps = {
        handleToggle,
        handleChange,
        handle24Hours,
        handleDeleteHours,
        handleAddPeriod,
        disabled,
        getValidationErrors,
      };

      return isSpecial ? (
        <HolidayHoursInputView
          schedules={transformedSchedules}
          timeOptions={timeOptions}
          {...inputViewProps}
        />
      ) : (
        <StandardHoursInputView
          compact={compact}
          visibleSchedules={transformedSchedules}
          timeOptions={timeOptions}
          dayOptions={dayOptions}
          {...inputViewProps}
        />
      );
    }

    // Timeline view
    return isSpecial ? (
      <HolidayTimeline
        schedules={transformedSchedules}
        timeOptions={timeOptions}
        handleToggle={handleToggle}
        handle24Hours={handle24Hours}
        handleDeleteHours={handleDeleteHours}
        handleChange={handleChange}
        handleTimeRangeChange={handleTimeRangeChange}
        disabled={disabled}
        getValidationErrors={getValidationErrors}
      />
    ) : (
      <StandardTimeline
        schedules={transformedSchedules}
        timeOptions={timeOptions}
        handleToggle={handleToggle}
        handle24Hours={handle24Hours}
        handleDeleteHours={handleDeleteHours}
        handleAddPeriod={handleAddPeriod}
        handleTimeRangeChange={handleTimeRangeChange}
        disabled={disabled}
        timelineColor={timelineColor}
        getValidationErrors={getValidationErrors}
      />
    );
  };

  return (
    <Stack gap="md">
      {hasRollover && viewMode === VIEW_MODE.INPUT && (
        <Alert variant="gray" visibleFrom="md">
          <Text>
            There are rollover hours in your schedule.
            {!compact &&
              ' Timeline view is not compatible with rollover hours and will be disabled.'}
          </Text>
        </Alert>
      )}

      {/* Only show schedules if we have any data or if it's special hours */}
      {(hasAnySchedules || isSpecial) && renderContent()}

      {/* Show Add Hours if no schedules exist in standard mode */}
      {/* or always show Add Holiday if in special mode */}
      {showAddButton && ((!isSpecial && !hasAnySchedules) || isSpecial) && (
        <Button
          variant="link"
          radius={0}
          h="auto"
          lh="var(--mantine-line-height-md)"
          p={0}
          bg="transparent"
          tt="none"
          td="none"
          onClick={isSpecial ? handleAddHoliday : handleAddHours}
          leftSection={<PlusCircleIconSm />}
        >
          {isSpecial ? 'Add holiday' : 'Add hours'}
        </Button>
      )}
    </Stack>
  );
}

ScheduleSelect.propTypes = {
  isSpecial: PropTypes.bool,
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      endTime: PropTypes.string,
      startTime: PropTypes.string,
      uuid: PropTypes.string,
      tempId: PropTypes.string,
    }),
  ),
  setSchedules: PropTypes.func,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODE)).isRequired,
  showAddButton: PropTypes.bool,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  timelineColor: PropTypes.string,
  getValidationErrors: PropTypes.func,
  onRolloverChange: PropTypes.func,
};
